import * as React from 'react'
import Grid from '@mui/material/Grid'
import { currencyNumber, currencyString } from '@sales-center/common'
import { ILeadOppProductFormModel, serviceTotal, total } from './typings'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'

interface Props {
  values: ILeadOppProductFormModel
  initialTax: number
  initialTotal: number
  recurringTax: number
  recurringTotal: number
  isCanadianTax: boolean
  initialGSTTax: number
  initialPSTTax: number
  recurringGSTTax: number
  recurringPSTTax: number
}

export const AddServiceFormFooter: React.FC<Props> = (props) => {
  const {
    initialTax,
    initialTotal,
    recurringTax,
    recurringTotal,
    isCanadianTax,
    initialGSTTax,
    initialPSTTax,
    recurringGSTTax,
    recurringPSTTax,
    values: {
      chargeTax,
      annualOccurrences,
      quantity,
      initialPrice: initialPriceStr,
      recurringPrice: recurringPriceStr,
      subtractOne,
      totalFirst,
      totalSubsequent,
      gstHstLabel,
      pstQstLabel,
    },
  } = props

  const isXS = useMediaQuery('(max-width:480px)')
  const qty = quantity > 0 ? quantity : 1
  const frequency = subtractOne ? annualOccurrences - 1 : annualOccurrences

  const initialPrice = currencyNumber(initialPriceStr)
  const recurringPrice = currencyNumber(recurringPriceStr)

  const taxTotal = isCanadianTax ? initialGSTTax + initialPSTTax : initialTax

  const recurringTaxTotal = isCanadianTax
    ? recurringGSTTax + recurringPSTTax
    : recurringTax

  const initialFullPrice = total(initialPrice, taxTotal, false)

  const recurringFullPrice = total(recurringPrice, recurringTaxTotal, false)

  const initialDiscount = initialPrice - initialTotal

  const recurringDiscount = (recurringPrice - recurringTotal) * frequency

  const totalDiscount =
    initialDiscount + (recurringDiscount > 0 ? recurringDiscount : 0) * qty

  const totalTax = serviceTotal(taxTotal, recurringTaxTotal, frequency, qty)

  const totalList = serviceTotal(
    initialFullPrice,
    recurringFullPrice,
    frequency,
    qty,
  )

  const percent =
    (totalList === 0 || totalList === totalFirst
      ? 0
      : 1 - totalFirst / totalList) * 100

  const percentStr = percent.toLocaleString(undefined, {
    maximumFractionDigits: 0,
  })

  return (
    <>
      <Typography variant="h7" mt={2} mb={2}>
        Pricing Summary
      </Typography>

      <Grid
        container
        style={{
          padding: 20,
          backgroundColor: '#F9FAFB',
          marginBottom: 20,
        }}
      >
        <Grid container item xs direction="column">
          <Grid item>
            <Typography variant="body1">Total Discount</Typography>
          </Grid>
          <Grid item>
            <Typography variant="subtitle3">{`$${currencyString(totalDiscount)} (${percentStr}%)`}</Typography>
          </Grid>
        </Grid>
        {!isXS && chargeTax && (
          <Grid container item xs direction="column">
            <Grid item>
              <Typography variant="body1">{`${
                !isCanadianTax ? 'Sales' : ''
              } Tax`}</Typography>
            </Grid>
            <Grid item>
              <Typography variant="subtitle3" whiteSpace="break-spaces">
                {isCanadianTax
                  ? `${gstHstLabel} $${currencyString(
                      initialGSTTax + recurringGSTTax,
                    )} \n${pstQstLabel} $${currencyString(
                      initialPSTTax + recurringPSTTax,
                    )}`
                  : `$${currencyString(totalTax)}`}
              </Typography>
            </Grid>
          </Grid>
        )}
        <Grid container item xs={isXS ? 6 : 3} direction="column">
          <Grid item>
            <Typography variant="body1">First Year</Typography>
          </Grid>
          <Grid item>
            <Typography variant="subtitle3">
              ${currencyString(totalFirst)}
            </Typography>
          </Grid>
        </Grid>
        {isXS && chargeTax && (
          <Grid container item xs={6} direction="column" sx={{ mt: 1 }}>
            <Grid item>
              <Typography variant="body1">{`${
                !isCanadianTax ? 'Sales' : ''
              } Tax`}</Typography>
            </Grid>
            <Grid item>
              <Typography variant="subtitle3" whiteSpace="break-spaces">
                {isCanadianTax
                  ? `${gstHstLabel} $${currencyString(
                      initialGSTTax + recurringGSTTax,
                    )} \n${pstQstLabel} $${currencyString(
                      initialPSTTax + recurringPSTTax,
                    )}`
                  : `$${currencyString(totalTax)}`}
              </Typography>
            </Grid>
          </Grid>
        )}
        {annualOccurrences > 0 && (
          <Grid container item xs direction="column" sx={{ mt: isXS ? 1 : 0 }}>
            <Grid item>
              <Typography variant="body1">Subsequent Years</Typography>
            </Grid>
            <Grid item>
              <Typography variant="subtitle3">
                ${currencyString(totalSubsequent)}
              </Typography>
            </Grid>
          </Grid>
        )}
      </Grid>
    </>
  )
}
AddServiceFormFooter.displayName = 'AddServiceFormFooter'
